import { Link, useParams, Navigate } from "react-router-dom";
import { MapPin, ArrowRight, ShieldCheck } from "lucide-react";
import { Seo } from "../components/Seo";
import { Reveal } from "../components/Reveal";
import { PageHero } from "../components/PageHero";
import { FaqAccordion } from "../components/FaqAccordion";
import { CtaBand } from "../components/CtaBand";
import { SERVICES } from "../data/services";
import { CITIES } from "../data/cities";
import { CITY_IMAGES, cityFaqs } from "../data/site";

export default function CityDetail() {
    const { slug } = useParams();
    const city = CITIES.find((c) => c.slug === slug);
    if (!city) return <Navigate to="/locations" replace />;
    const idx = CITIES.indexOf(city);
    const nearby = [CITIES[(idx + 1) % CITIES.length], CITIES[(idx + 7) % CITIES.length], CITIES[(idx + 13) % CITIES.length]];

    return (
        <div data-testid={`city-page-${city.slug}`}>
            <Seo
                title={`Security Guards ${city.name} | SIA Licensed Security Services ${city.name} | Xtreme Protection`}
                description={`SIA-licensed security guards in ${city.name}, Scotland. Manned guarding, mobile patrols, CCTV monitoring, door supervisors & event security across ${city.name} — 24/7 cover from Xtreme Protection.`}
            />
            <PageHero
                image={CITY_IMAGES[idx % CITY_IMAGES.length]}
                imageAlt={`${city.name} at night — security guard services ${city.name} Scotland`}
                kicker={`Scotland Security Operations // ${city.region}`}
                title={`Security Guards`}
                titleAccent={city.name}
                sub={`SIA-licensed security services across ${city.name} and ${city.region} — manned guarding, mobile patrols, CCTV monitoring, event security and 13 more specialist services, backed by our 24/7 Scottish command centre.`}
                testidPrefix={`city-${city.slug}`}
            />

            <section className="max-w-7xl mx-auto px-4 sm:px-6 py-20 grid lg:grid-cols-3 gap-14">
                <div className="lg:col-span-2">
                    <Reveal>
                        <p className="font-mono-xp text-[11px] uppercase tracking-[0.3em] text-gray-500 mb-4">Local Coverage</p>
                        <h2 className="font-display uppercase font-extrabold text-3xl sm:text-4xl tracking-wide text-white leading-none">
                            Security services in <span className="text-[#E53E3E]">{city.name}</span>
                        </h2>
                        <p className="text-gray-300 text-sm sm:text-base leading-relaxed mt-6">{city.p1}</p>
                        <p className="text-gray-400 text-sm sm:text-base leading-relaxed mt-4">{city.p2}</p>
                    </Reveal>
                    <Reveal delay={0.1} className="mt-10">
                        <h3 className="font-display uppercase font-bold text-xl tracking-wide text-white mb-5">
                            Areas we cover in {city.name}
                        </h3>
                        <div className="flex flex-wrap gap-2.5" data-testid={`city-areas-${city.slug}`}>
                            {city.areas.map((a) => (
                                <span key={a} className="flex items-center gap-2 font-mono-xp text-[11px] uppercase tracking-wider text-gray-300 border border-white/10 px-3.5 py-2">
                                    <MapPin size={12} className="text-[#E53E3E]" /> {a}
                                </span>
                            ))}
                        </div>
                    </Reveal>
                    <Reveal delay={0.15} className="mt-12">
                        <h3 className="font-display uppercase font-bold text-xl tracking-wide text-white mb-5">
                            {city.name} security questions, answered
                        </h3>
                        <FaqAccordion faqs={cityFaqs(city.name)} testidPrefix={`city-faq-${city.slug}`} />
                    </Reveal>
                </div>
                <aside>
                    <Reveal delay={0.1}>
                        <div className="bg-[#0F1117] border border-white/10 p-6">
                            <h3 className="font-display uppercase tracking-wider text-white text-lg mb-4">Why {city.name} trusts us</h3>
                            <ul className="space-y-3.5">
                                {["SIA-licensed officers vetted to BS 7858", "GPS and NFC-verified patrol reporting", "Local teams who know your streets", "24/7 Scottish command centre", "Free on-site risk assessment"].map((t) => (
                                    <li key={t} className="flex items-start gap-3 text-sm text-gray-300">
                                        <ShieldCheck size={15} className="text-[#E53E3E] mt-0.5 shrink-0" /> {t}
                                    </li>
                                ))}
                            </ul>
                        </div>
                    </Reveal>
                    <Reveal delay={0.18} className="mt-6">
                        <div className="bg-[#0F1117] border border-white/10 p-6">
                            <h3 className="font-display uppercase tracking-wider text-white text-lg mb-4">Nearby Coverage</h3>
                            <ul className="space-y-3">
                                {nearby.map((n) => (
                                    <li key={n.slug}>
                                        <Link
                                            to={`/locations/${n.slug}`}
                                            data-testid={`city-nearby-${city.slug}-${n.slug}`}
                                            className="group flex items-center justify-between text-sm text-gray-400 hover:text-[#E53E3E] transition-colors"
                                        >
                                            Security {n.name}
                                            <ArrowRight size={13} className="group-hover:translate-x-1 transition-transform" />
                                        </Link>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    </Reveal>
                </aside>
            </section>

            <section className="bg-[#0F1117] border-y border-white/10 py-20" data-testid={`city-services-${city.slug}`}>
                <div className="max-w-7xl mx-auto px-4 sm:px-6">
                    <Reveal>
                        <p className="font-mono-xp text-[11px] uppercase tracking-[0.3em] text-gray-500 mb-4">The Arsenal // {city.name}</p>
                        <h2 className="font-display uppercase font-extrabold text-3xl sm:text-4xl tracking-wide text-white leading-none">
                            17 security services available in <span className="text-[#E53E3E]">{city.name}</span>
                        </h2>
                    </Reveal>
                    <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-px bg-white/10 border border-white/10 mt-10">
                        {SERVICES.map((s) => (
                            <Link
                                key={s.slug}
                                to={`/services/${s.slug}`}
                                data-testid={`city-service-link-${city.slug}-${s.slug}`}
                                className="group flex items-center justify-between bg-[#0F1117] hover:bg-[#161922] transition-colors duration-300 px-5 py-4"
                            >
                                <span className="text-sm text-gray-300 group-hover:text-white transition-colors">{s.name} {city.name}</span>
                                <ArrowRight size={13} className="text-gray-700 group-hover:text-[#E53E3E] group-hover:translate-x-1 transition-all duration-300 shrink-0" />
                            </Link>
                        ))}
                    </div>
                </div>
            </section>

            <CtaBand title={`Secure your ${city.name} site today`} />
        </div>
    );
}
